import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../../i18n';

const normalizeMessage = (message) =>
  typeof message === 'string'
    ? message
        .replace(/\s+/g, ' ')
        .replace(/\s+,/g, ',')
        .replace(/,\s+/g, ', ')
        .trim()
    : '';

const SUCCESS_MESSAGE_KEY_MAP = {
  'License added successfully': 'licenses.success.created',
  'License created successfully': 'licenses.success.created',
  'License updated successfully': 'licenses.success.updated',
  'License deleted successfully': 'licenses.success.deleted',
  'Licenses deleted successfully': 'licenses.success.bulkDeleted',
  'License approved successfully': 'licenses.success.approved',
  'License rejected successfully': 'licenses.success.rejected',
  'License extended successfully': 'licenses.success.extended',
  'License revoked successfully': 'licenses.success.revoked',
  'Unit assigned successfully': 'licenses.success.assignedToUnit',
  'Licenses imported successfully': 'licenses.success.imported',
  'Template downloaded successfully': 'licenses.templateDownloaded',
  'License template downloaded successfully': 'licenses.templateDownloaded',
};

const translateSuccessMessage = (message, defaultKey, options = {}) => {
  const normalized = normalizeMessage(message);
  if (normalized) {
    const mappedKey = SUCCESS_MESSAGE_KEY_MAP[normalized];
    if (mappedKey) {
      return i18n.t(mappedKey, options);
    }
    if (typeof i18n.exists === 'function' && i18n.exists(normalized)) {
      return i18n.t(normalized, options);
    }
    return normalized;
  }
  return i18n.t(defaultKey, options);
};

const initialState = {
  licenses: [],
  licenseStatuses: [],
  licenseTypes: [], // License types from init endpoint for filter
  availableUnits: [], // Units from init endpoint for filter
  initLicense: null,
  canReview: false,
  units: [], // Units for assign modal
  unitsTotal: 0,
  unitsPage: 1,
  unitsPageSize: 10,
  unitsLoading: false,
  total: 0,
  page: 1,
  pageSize: 10,
  sortColumn: 'code',
  sortDirection: 'asc',
  filterCode: '',
  filterStatuses: [], // Multi-select status filter
  filterLicenseTypes: [], // Multi-select license type filter
  filterUnits: [], // Multi-select unit filter
  filterExpiryDateFrom: '', // Expiry date range filter - from
  filterExpiryDateTo: '', // Expiry date range filter - to
  loading: false,
  error: null,
  successMessage: null,
  selectedLicense: null,
};

const licensesSlice = createSlice({
  name: 'licenses',
  initialState,
  reducers: {
    // Fetch init data
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.licenseStatuses = action.payload.licenseStatuses || [];
      state.licenseTypes = action.payload.licenseTypes || [];
      state.availableUnits = action.payload.units || [];
      state.initLicense = action.payload.initLicense || null;
      state.canReview = action.payload.canReview || false;
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch licenses
    fetchLicenses: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchLicensesSuccess: (state, action) => {
      state.loading = false;
      state.licenses = action.payload.data;
      state.total = action.payload.meta.total;
      state.error = null;
    },
    fetchLicensesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch license by ID
    fetchLicenseById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchLicenseByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedLicense = action.payload;
      state.error = null;
    },
    fetchLicenseByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Add license
    addLicense: (state) => {
      state.loading = true;
      state.error = null;
    },
    addLicenseSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.created');
      state.error = null;
    },
    addLicenseFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update license
    updateLicense: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateLicenseSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.updated');
      state.error = null;
    },
    updateLicenseFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete license
    deleteLicense: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteLicenseSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.deleted');
      state.error = null;
    },
    deleteLicenseFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Bulk delete
    bulkDeleteLicenses: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteLicensesSuccess: (state, action) => {
      state.loading = false;
      const count =
        action.payload?.count ??
        action.payload?.length ??
        action.payload?.deletedIds?.length ??
        action.payload?.data?.length ??
        0;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.bulkDeleted', {
        count,
      });
      state.error = null;
    },
    bulkDeleteLicensesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1;
    },

    // Sorting
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    // Filtering
    setFilterCode: (state, action) => {
      state.filterCode = action.payload;
      state.page = 1;
    },
    setFilterStatuses: (state, action) => {
      state.filterStatuses = action.payload;
      state.page = 1;
    },
    setFilterLicenseTypes: (state, action) => {
      state.filterLicenseTypes = action.payload;
      state.page = 1;
    },
    setFilterUnits: (state, action) => {
      state.filterUnits = action.payload;
      state.page = 1;
    },
    setFilterExpiryDateFrom: (state, action) => {
      state.filterExpiryDateFrom = action.payload;
      state.page = 1;
    },
    setFilterExpiryDateTo: (state, action) => {
      state.filterExpiryDateTo = action.payload;
      state.page = 1;
    },

    // Review actions
    approveLicense: (state) => {
      state.loading = true;
      state.error = null;
    },
    approveLicenseSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.approved');
      state.error = null;
    },
    approveLicenseFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    rejectLicense: (state) => {
      state.loading = true;
      state.error = null;
    },
    rejectLicenseSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.rejected');
      state.error = null;
    },
    rejectLicenseFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    extendLicense: (state) => {
      state.loading = true;
      state.error = null;
    },
    extendLicenseSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.extended');
      state.error = null;
    },
    extendLicenseFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    revokeLicense: (state) => {
      state.loading = true;
      state.error = null;
    },
    revokeLicenseSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.revoked');
      state.error = null;
    },
    revokeLicenseFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch units for assignment
    fetchUnits: (state) => {
      state.unitsLoading = true;
      state.error = null;
    },
    fetchUnitsSuccess: (state, action) => {
      state.unitsLoading = false;
      state.units = action.payload.data;
      state.unitsTotal = action.payload.meta.total;
      state.error = null;
    },
    fetchUnitsFailure: (state, action) => {
      state.unitsLoading = false;
      state.error = action.payload;
    },

    // Assign unit to license
    assignUnit: (state) => {
      state.loading = true;
      state.error = null;
    },
    assignUnitSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.assignedToUnit');
      state.error = null;
    },
    assignUnitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Import licenses
    importLicenses: (state) => {
      state.loading = true;
      state.error = null;
    },
    importLicensesSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.imported');
      state.error = null;
    },
    importLicensesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Export licenses
    exportLicenses: (state) => {
      state.loading = true;
      state.error = null;
    },
    exportLicensesSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.success.exported');
      state.error = null;
    },
    exportLicensesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Download template
    downloadTemplate: (state) => {
      state.loading = true;
      state.error = null;
    },
    downloadTemplateSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = translateSuccessMessage(action.payload?.message, 'licenses.templateDownloaded');
      state.error = null;
    },
    downloadTemplateFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Set units pagination
    setUnitsPage: (state, action) => {
      state.unitsPage = action.payload;
    },
    setUnitsPageSize: (state, action) => {
      state.unitsPageSize = action.payload;
      state.unitsPage = 1;
    },

    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearSelectedLicense: (state) => {
      state.selectedLicense = null;
    },
  },
});

export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchLicenses,
  fetchLicensesSuccess,
  fetchLicensesFailure,
  fetchLicenseById,
  fetchLicenseByIdSuccess,
  fetchLicenseByIdFailure,
  addLicense,
  addLicenseSuccess,
  addLicenseFailure,
  updateLicense,
  updateLicenseSuccess,
  updateLicenseFailure,
  deleteLicense,
  deleteLicenseSuccess,
  deleteLicenseFailure,
  bulkDeleteLicenses,
  bulkDeleteLicensesSuccess,
  bulkDeleteLicensesFailure,
  approveLicense,
  approveLicenseSuccess,
  approveLicenseFailure,
  rejectLicense,
  rejectLicenseSuccess,
  rejectLicenseFailure,
  extendLicense,
  extendLicenseSuccess,
  extendLicenseFailure,
  revokeLicense,
  revokeLicenseSuccess,
  revokeLicenseFailure,
  fetchUnits,
  fetchUnitsSuccess,
  fetchUnitsFailure,
  assignUnit,
  assignUnitSuccess,
  assignUnitFailure,
  importLicenses,
  importLicensesSuccess,
  importLicensesFailure,
  exportLicenses,
  exportLicensesSuccess,
  exportLicensesFailure,
  downloadTemplate,
  downloadTemplateSuccess,
  downloadTemplateFailure,
  setUnitsPage,
  setUnitsPageSize,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterCode,
  setFilterStatuses,
  setFilterLicenseTypes,
  setFilterUnits,
  setFilterExpiryDateFrom,
  setFilterExpiryDateTo,
  clearError,
  clearSuccessMessage,
  clearSelectedLicense,
} = licensesSlice.actions;

// Selectors
export const selectLicenses = (state) => state.licenses?.licenses || [];
export const selectLicenseStatuses = (state) => state.licenses?.licenseStatuses || [];
export const selectLicenseTypes = (state) => state.licenses?.licenseTypes || [];
export const selectAvailableUnits = (state) => state.licenses?.availableUnits || [];
export const selectInitLicense = (state) => state.licenses?.initLicense;
export const selectCanReview = (state) => state.licenses?.canReview || false;
export const selectSelectedLicense = (state) => state.licenses?.selectedLicense;
export const selectLicensesLoading = (state) => state.licenses?.loading || false;
export const selectLicensesError = (state) => state.licenses?.error;
export const selectLicensesSuccessMessage = (state) => state.licenses?.successMessage;
export const selectLicensesTotal = (state) => state.licenses?.total || 0;
export const selectLicensesPage = (state) => state.licenses?.page || 1;
export const selectLicensesPageSize = (state) => state.licenses?.pageSize || 10;
export const selectLicensesSortColumn = (state) => state.licenses?.sortColumn || 'code';
export const selectLicensesSortDirection = (state) => state.licenses?.sortDirection || 'asc';
export const selectLicensesFilterCode = (state) => state.licenses?.filterCode || '';
export const selectLicensesFilterStatuses = (state) => state.licenses?.filterStatuses || [];
export const selectLicensesFilterLicenseTypes = (state) => state.licenses?.filterLicenseTypes || [];
export const selectLicensesFilterUnits = (state) => state.licenses?.filterUnits || [];
export const selectLicensesFilterExpiryDateFrom = (state) => state.licenses?.filterExpiryDateFrom || '';
export const selectLicensesFilterExpiryDateTo = (state) => state.licenses?.filterExpiryDateTo || '';
export const selectUnits = (state) => state.licenses?.units || [];
export const selectUnitsTotal = (state) => state.licenses?.unitsTotal || 0;
export const selectUnitsPage = (state) => state.licenses?.unitsPage || 1;
export const selectUnitsPageSize = (state) => state.licenses?.unitsPageSize || 10;
export const selectUnitsLoading = (state) => state.licenses?.unitsLoading || false;

export default licensesSlice.reducer;
